import {
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { HelpOutline } from "@mui/icons-material";
import axios from "axios";
import { useEffect, useState } from "react";
import { hostname } from "../../utils";

export default ({
  actionParameters,
  setActionParameters,
  headers,
  existingRecord,
  setValidForm,
  getToken,
  companyId,
}: {
  actionParameters: any;
  setActionParameters: (p: any) => void;
  headers: Array<any>;
  existingRecord: boolean;
  setValidForm: (b: boolean) => void;
  getToken: Function;
  companyId: string;
}) => {
  const [browserIntegrations, setBrowserIntegrations] = useState<Array<any>>(
    []
  );
  const [loadingIntegrations, setLoadingIntegrations] = useState(false);

  // Fetch browser session integrations
  useEffect(() => {
    if (!getToken || !companyId) return;

    const fetchIntegrations = async () => {
      setLoadingIntegrations(true);
      try {
        const token = await getToken();
        const response = await axios.get(
          `${hostname}/integrations/by-type/browser_session`,
          {
            params: { companyId },
            headers: { Authorization: `Bearer ${token}` },
          }
        );

        // Filter for browser_session integrations
        const browserSessionIntegrations = response.data.items || [];
        setBrowserIntegrations(browserSessionIntegrations);
      } catch (error) {
        console.error("Error fetching integrations:", error);
      } finally {
        setLoadingIntegrations(false);
      }
    };

    fetchIntegrations();
  }, [getToken, companyId]);

  useEffect(() => {
    // Set default session type if not set
    if (!actionParameters.sessionType) {
      setActionParameters({
        ...actionParameters,
        sessionType: "none",
      });
    }

    setValidForm(
      actionParameters.task && actionParameters.task.trim().length > 0
    );
  }, [actionParameters]);

  return (
    <div>
      <div style={{ marginTop: "16px" }}>
        <Typography variant="subtitle1" sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          Task Description
          <Tooltip title="Describe what the browser should do step-by-step. Use {{Column Name}} to insert row data like URLs or search terms. The AI will control a real browser to complete your task.">
            <HelpOutline sx={{ fontSize: 16, color: "text.secondary", cursor: "help" }} />
          </Tooltip>
        </Typography>
        <Typography variant="body2">
          Describe the browser task you want to perform. Use{" "}
          <code>{"{{columnName}}"}</code> to insert values from the row.
        </Typography>
        <TextField
          fullWidth
          multiline
          rows={6}
          value={actionParameters.task || ""}
          onChange={(e) =>
            setActionParameters({ ...actionParameters, task: e.target.value })
          }
          placeholder="Example: Visit {{websiteUrl}} and extract the contact information"
          variant="outlined"
          style={{ marginTop: "16px" }}
        />
      </div>

      <div style={{ marginTop: "24px" }}>
        <Typography variant="subtitle1" sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          Session Type
          <Tooltip title="Basic: Standard browser, good for public pages. Residential Proxy: Uses a US residential IP to avoid blocks. Browser Session: Uses a saved session with cookies/login state for authenticated pages.">
            <HelpOutline sx={{ fontSize: 16, color: "text.secondary", cursor: "help" }} />
          </Tooltip>
        </Typography>
        <Typography variant="body2">
          Choose how to run the browser task
        </Typography>
        <FormControl fullWidth style={{ marginTop: 16 }}>
          <InputLabel>Session Type</InputLabel>
          <Select
            value={actionParameters.sessionType || "none"}
            label="Session Type"
            onChange={(e) => {
              const newSessionType = e.target.value;
              setActionParameters({
                ...actionParameters,
                sessionType: newSessionType,
                // Clear integration ID if switching away from integration mode
                ...(newSessionType !== "integration"
                  ? { browserIntegrationId: null }
                  : {}),
                // Clear zipcode if switching away from residential
                ...(newSessionType !== "residential" ? { zipcode: null } : {}),
              });
            }}
          >
            <MenuItem value="none">No Session (Basic)</MenuItem>
            <MenuItem value="residential">Residential Proxy</MenuItem>
            <MenuItem value="integration">Browser Session Integration</MenuItem>
          </Select>
          <FormHelperText>
            Basic: Standard browser | Residential: US proxy | Integration: Saved
            session
          </FormHelperText>
        </FormControl>
      </div>

      {/* Residential Proxy Configuration */}
      {actionParameters.sessionType === "residential" && (
        <div style={{ marginTop: "16px" }}>
          <Typography variant="subtitle1" sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            Zip Code
            <Tooltip title="The proxy will appear to come from this US location. Useful for location-specific content or to match your target audience's region.">
              <HelpOutline sx={{ fontSize: 16, color: "text.secondary", cursor: "help" }} />
            </Tooltip>
          </Typography>
          <Typography variant="body2">
            Enter a 5-digit US zip code for the residential proxy location
          </Typography>
          <TextField
            fullWidth
            value={actionParameters.zipcode || ""}
            onChange={(e) =>
              setActionParameters({
                ...actionParameters,
                zipcode: e.target.value,
              })
            }
            placeholder="e.g., 94102"
            variant="outlined"
            style={{ marginTop: "16px" }}
          />
        </div>
      )}

      {/* Browser Session Integration Configuration */}
      {actionParameters.sessionType === "integration" && (
        <div style={{ marginTop: "16px" }}>
          <Typography variant="subtitle1" sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            Browser Session Integration
            <Tooltip title="Browser sessions save cookies and login state. Use this to access pages that require authentication without re-logging in each time.">
              <HelpOutline sx={{ fontSize: 16, color: "text.secondary", cursor: "help" }} />
            </Tooltip>
          </Typography>
          <Typography variant="body2">
            Select a saved browser session to use
          </Typography>
          <FormControl
            fullWidth
            style={{ marginTop: 16 }}
            disabled={loadingIntegrations}
          >
            <InputLabel>Browser Session Integration</InputLabel>
            <Select
              value={actionParameters.browserIntegrationId || ""}
              label="Browser Session Integration"
              onChange={(e) =>
                setActionParameters({
                  ...actionParameters,
                  browserIntegrationId: e.target.value,
                })
              }
            >
              {browserIntegrations.length === 0 && !loadingIntegrations && (
                <MenuItem value="" disabled>
                  No browser session integrations found
                </MenuItem>
              )}
              {loadingIntegrations && (
                <MenuItem value="" disabled>
                  Loading integrations...
                </MenuItem>
              )}
              {browserIntegrations.map((integration) => (
                <MenuItem key={integration.id} value={integration.id}>
                  {integration.instanceName || integration.name || "Unnamed"}
                </MenuItem>
              ))}
            </Select>
            <FormHelperText>
              Browser sessions preserve cookies and login state
            </FormHelperText>
          </FormControl>
        </div>
      )}
    </div>
  );
};
